import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  getGroupsByUser,
  getGroupAdherents,
} from "../../../../services/groupServices";

import {
  getEventsByAdherent,
  updateAdherentResults,
} from "../../../../services/eventServices";

import { useUser } from "../../../Home/UserContext";
import { useNotification } from "../../../../components/Notification/NotificationManager";

import {
  DEFAULT_RESULT_FORM,
  buildResultForm,
  buildResultPayload,
  buildResultsStats,
  getResultKey,
  getUserId,
  sortResultsByDateDesc,
} from "../utils/groupResultsUtils";

const DEFAULT_FILTERS = {
  search: "",
  adherentId: "",
  groupId: "",
  eventTitle: "",
  status: "all",
};

export function useGroupResultsPage() {
  const { user } = useUser();
  const { addNotification } = useNotification();

  const notificationRef = useRef(addNotification);

  const [groups, setGroups] = useState([]);
  const [approvedAdherents, setApprovedAdherents] = useState([]);
  const [results, setResults] = useState([]);

  const [filters, setFilters] = useState(DEFAULT_FILTERS);

  const [selectedResult, setSelectedResult] = useState(null);
  const [form, setForm] = useState(DEFAULT_RESULT_FORM);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [savingKey, setSavingKey] = useState("");
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [imagePreview, setImagePreview] = useState("");

  const userId = useMemo(() => getUserId(user), [user]);

  useEffect(() => {
    notificationRef.current = addNotification;
  }, [addNotification]);

  const fetchResults = useCallback(
    async ({ silent = false } = {}) => {
      if (!userId) {
        setGroups([]);
        setApprovedAdherents([]);
        setResults([]);
        setLoading(false);
        return;
      }

      if (results.length > 0) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      try {
        const userGroups = await getGroupsByUser(userId);
        const safeGroups = Array.isArray(userGroups) ? userGroups : [];

        setGroups(safeGroups);

        const groupEntries = await Promise.all(
          safeGroups.map(async (group) => {
            try {
              const response = await getGroupAdherents(group.id);
              const groupAdherents = Array.isArray(response?.adherents)
                ? response.adherents
                : [];

              return groupAdherents
                .filter((adherent) => adherent.status === "approved")
                .map((adherent) => ({
                  ...adherent,
                  group,
                  groupId: group.id,
                  groupName: group.nom,
                }));
            } catch (error) {
              console.error(
                `Erreur lors du chargement des adhérents du groupe ${group.id} :`,
                error,
              );

              return [];
            }
          }),
        );

        const adherents = groupEntries.flat();

        setApprovedAdherents(adherents);

        const resultEntries = await Promise.all(
          adherents.map(async (adherent) => {
            try {
              const events = await getEventsByAdherent(adherent.id);
              const safeEvents = Array.isArray(events?.Events)
                ? events.Events
                : [];

              return safeEvents.map((event) => ({
                adherent,
                group: adherent.groupName,
                groupId: adherent.groupId,
                event,
              }));
            } catch (error) {
              console.error(
                `Erreur lors du chargement des résultats de l'adhérent ${adherent.id} :`,
                error,
              );

              return [];
            }
          }),
        );

        const orderedResults = sortResultsByDateDesc(resultEntries.flat());

        setResults(orderedResults);

        if (!silent) {
          notificationRef.current?.("Résultats actualisés.", "success");
        }
      } catch (error) {
        console.error("Erreur lors de la récupération des résultats :", error);

        notificationRef.current?.(
          "Erreur lors de la récupération des résultats.",
          "error",
        );
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [results.length, userId],
  );

  useEffect(() => {
    fetchResults({ silent: true });
  }, [fetchResults]);

  const filteredResults = useMemo(() => {
    const search = filters.search.trim().toLowerCase();

    return results.filter((result) => {
      const adherentName = `${result.adherent?.prenom || ""} ${
        result.adherent?.nom || ""
      }`.toLowerCase();

      const eventTitle = String(result.event?.titre || "").toLowerCase();
      const groupName = String(result.group || "").toLowerCase();

      const matchesSearch =
        !search ||
        adherentName.includes(search) ||
        eventTitle.includes(search) ||
        groupName.includes(search);

      const matchesAdherent =
        !filters.adherentId ||
        String(result.adherent?.id) === String(filters.adherentId);

      const matchesGroup =
        !filters.groupId || String(result.groupId) === String(filters.groupId);

      const matchesEvent =
        !filters.eventTitle || result.event?.titre === filters.eventTitle;

      const hasResults = Boolean(result.event?.AdherentEvent?.resultats);

      const matchesStatus =
        filters.status === "all" ||
        (filters.status === "filled" && hasResults) ||
        (filters.status === "empty" && !hasResults);

      return (
        matchesSearch &&
        matchesAdherent &&
        matchesGroup &&
        matchesEvent &&
        matchesStatus
      );
    });
  }, [filters, results]);

  const stats = useMemo(() => buildResultsStats(results), [results]);

  const updateFilters = useCallback((name, value) => {
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  const resetFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
  }, []);

  const updateResultInState = useCallback((targetResult, payload) => {
    const targetKey = getResultKey(targetResult);

    setResults((prev) =>
      prev.map((result) => {
        if (getResultKey(result) !== targetKey) return result;

        return {
          ...result,
          event: {
            ...result.event,
            AdherentEvent: {
              ...result.event.AdherentEvent,
              adherent_id: payload.adherent_id,
              event_id: payload.event_id,
              resultats: payload.resultats,
              classement: payload.classement,
              observations: payload.observations,
              podium_url: payload.podium_url,
              tableau_url: payload.tableau_url,
            },
          },
        };
      }),
    );
  }, []);

  const persistResult = useCallback(
    async (targetResult, nextForm, actionKey = "save-result") => {
      if (!targetResult?.event?.AdherentEvent && !targetResult?.event?.id) {
        notificationRef.current?.("Données de l'événement manquantes.", "error");
        return;
      }

      const payload = buildResultPayload(targetResult, nextForm);

      if (!payload.adherent_id || !payload.event_id) {
        notificationRef.current?.("Adhérent ou événement non identifié.", "error");
        return;
      }

      setSavingKey(actionKey);

      try {
        await updateAdherentResults(payload);
        updateResultInState(targetResult, payload);

        notificationRef.current?.("Résultat mis à jour.", "success");
      } catch (error) {
        console.error("Erreur lors de la mise à jour du résultat :", error);

        notificationRef.current?.(
          "Erreur lors de la mise à jour du résultat.",
          "error",
        );
      } finally {
        setSavingKey("");
      }
    },
    [updateResultInState],
  );

  const quickAddResult = useCallback(
    async (targetResult, token) => {
      const currentForm = buildResultForm(targetResult.event);
      const nextForm = {
        ...currentForm,
        resultats: `${currentForm.resultats || ""}${token}`,
      };

      await persistResult(targetResult, nextForm, `quick-${getResultKey(targetResult)}`);
    },
    [persistResult],
  );

  const quickRemoveLastResult = useCallback(
    async (targetResult) => {
      const currentForm = buildResultForm(targetResult.event);

      if (!currentForm.resultats) return;

      const nextForm = {
        ...currentForm,
        resultats: String(currentForm.resultats).slice(0, -1),
      };

      await persistResult(
        targetResult,
        nextForm,
        `remove-${getResultKey(targetResult)}`,
      );
    },
    [persistResult],
  );

  const openEditResult = useCallback((targetResult) => {
    setSelectedResult(targetResult);
    setForm(buildResultForm(targetResult.event));
    setIsEditOpen(true);
  }, []);

  const closeEditResult = useCallback(() => {
    setIsEditOpen(false);
    setSelectedResult(null);
    setForm(DEFAULT_RESULT_FORM);
  }, []);

  const updateFormField = useCallback((eventOrName, value) => {
    if (typeof eventOrName === "string") {
      setForm((prev) => ({
        ...prev,
        [eventOrName]: value,
      }));
      return;
    }

    const { name, value: fieldValue } = eventOrName.target;

    setForm((prev) => ({
      ...prev,
      [name]: fieldValue,
    }));
  }, []);

  const submitEditResult = useCallback(
    async (event) => {
      event.preventDefault();

      if (!selectedResult) {
        notificationRef.current?.("Aucun résultat sélectionné.", "error");
        return;
      }

      await persistResult(selectedResult, form, "edit-result");
      closeEditResult();
    },
    [closeEditResult, form, persistResult, selectedResult],
  );

  const openImagePreview = useCallback((src) => {
    if (!src) return;
    setImagePreview(src);
  }, []);

  const closeImagePreview = useCallback(() => {
    setImagePreview("");
  }, []);

  return {
    user,
    groups,
    approvedAdherents,
    results,
    filteredResults,
    filters,
    stats,
    loading,
    refreshing,
    savingKey,
    selectedResult,
    form,
    isEditOpen,
    imagePreview,
    fetchResults,
    updateFilters,
    resetFilters,
    openEditResult,
    closeEditResult,
    updateFormField,
    submitEditResult,
    openImagePreview,
    closeImagePreview,
  };
}